import { UUIDs } from "@/types/uuids";
import type { ChartDataItem } from "../../utils/chart-values";

export const timePoints = [
	"Baseline",
	"3 month",
	"6 month",
	"9 month",
	"1 year",
	"2 year",
	"3 year",
] as const;

export type TimePoint = (typeof timePoints)[number];

export interface WomacDataPoint {
	name: string;
	date: string;
	timing: string;
	"WOMAC Pain Score": number;
	"WOMAC Stiffness Score": number;
	"WOMAC Function Score": number;
}

export interface WomacEntryValue {
	input_id: string;
	input_label: string;
	average: number;
	entries_count: number;
	min_value: number;
	max_value: number;
	std_deviation: number;
}

export interface WomacEntry {
	schedule_index: number;
	schedule_id: string;
	timing_label: string;
	entry_values: WomacEntryValue[];
}

export interface WomacChartConfig {
	"WOMAC Pain Score": {
		label: string;
		color: string;
	};
	"WOMAC Stiffness Score": {
		label: string;
		color: string;
	};
	"WOMAC Function Score": {
		label: string;
		color: string;
	};
	"WOMAC Total Score": {
		label: string;
		color: string;
	};
}

export const womacChartConfig: WomacChartConfig = {
	"WOMAC Pain Score": {
		label: "Pain Score",
		color: "hsl(var(--chart-4))",
	},
	"WOMAC Stiffness Score": {
		label: "Stiffness Score",
		color: "hsl(var(--chart-2))",
	},
	"WOMAC Function Score": {
		label: "Function Score",
		color: "hsl(var(--chart-3))",
	},
	"WOMAC Total Score": {
		label: "Total Score",
		color: "hsl(var(--chart-1))",
	},
};

export function processWomacData(entries: WomacEntry[]): ChartDataItem[] {
	return entries.map((entry) => {
		// Find the total WOMAC score
		const totalScore = entry.entry_values.find(
			(ev) => ev.input_id === UUIDs.womacInputs.womacTotal,
		);

		// Find the pain score
		const painScore = entry.entry_values.find(
			(ev) => ev.input_id === UUIDs.womacInputs.womacPain,
		);

		// Find the stiffness score
		const stiffnessScore = entry.entry_values.find(
			(ev) => ev.input_id === UUIDs.womacInputs.womacStiffness,
		);

		// Find the function score
		const functionScore = entry.entry_values.find(
			(ev) => ev.input_id === UUIDs.womacInputs.womacFunction,
		);

		return {
			timing: entry.timing_label,
			date: entry.timing_label,
			schedule_index: entry.schedule_index,
			schedule_id: entry.schedule_id,
			entries_count: totalScore?.entries_count ?? 0,
			value: totalScore?.average ?? 0,
			"WOMAC Pain Score": painScore?.average ?? 0,
			"WOMAC Stiffness Score": stiffnessScore?.average ?? 0,
			"WOMAC Function Score": functionScore?.average ?? 0,
			"WOMAC Total Score": totalScore?.average ?? 0,
		};
	});
}

export function getWomacTotal(data: WomacDataPoint): number {
	return (
		data["WOMAC Pain Score"] +
		data["WOMAC Stiffness Score"] +
		data["WOMAC Function Score"]
	);
}

export function getWomacAverage(data: WomacDataPoint[]): WomacDataPoint {
	const avgPain =
		data.reduce((sum, d) => sum + d["WOMAC Pain Score"], 0) / data.length;
	const avgStiffness =
		data.reduce((sum, d) => sum + d["WOMAC Stiffness Score"], 0) / data.length;
	const avgFunction =
		data.reduce((sum, d) => sum + d["WOMAC Function Score"], 0) / data.length;

	return {
		name: "Average",
		date: "",
		timing: "Average",
		"WOMAC Pain Score": Number(avgPain.toFixed(2)),
		"WOMAC Stiffness Score": Number(avgStiffness.toFixed(2)),
		"WOMAC Function Score": Number(avgFunction.toFixed(2)),
	};
}

export function roundTo1DP(value: number): string {
	return value.toFixed(1);
}

export function formatPercentage(value: number): string {
	return `${Math.round(value * 100)}%`;
}
